import { useMutation } from "@tanstack/react-query";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { toast } from "react-toastify";
import { keycloakService } from "services/keycloakService";

interface DCAOrderPayload {
  units: number;
  securityCode: string;
  currency: string;
  contact: string;
  portfolioShortName: string;
  buyTime: number;
  unitAsset?: string;
}

interface DCAOrderResponse {
  id?: string;
  success?: boolean;
  message?: string;
}

const createDCAOrder = async (
  payload: DCAOrderPayload
): Promise<DCAOrderResponse> => {
  try {
    const token = await keycloakService.getToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const response = await fetch(process.env.REACT_APP_DCA_API_URL || "", {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating DCA order:", error);
    throw error;
  }
};

export const useCreateDCAOrder = () => {
  const { t } = useModifiedTranslation();

  const mutation = useMutation({
    mutationFn: createDCAOrder,
    onSuccess: () => {
      // DCA orders will be refetched when needed by Apollo Client
    },
    onError: (error: Error) => {
      console.error("DCA order creation failed:", error);
      toast.error(t("messages.dcaOrderFailed"));
    },
  });

  return {
    createDCAOrder: mutation.mutate,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};
